import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useUpdateEffect } from "react-use";
import PropTypes from "prop-types";
import { v4 } from "uuid";
import { getRawConfigOption } from "~/store/selectors/selector.configOptions";
import { isLoggedUserClientSelector } from "~/store/selectors/selector.users";
import { FormCheckbox } from "./FormCheckbox";

const useCheckboxToggle = (initialValue, onChange) => {
  const [isChecked, setIsChecked] = useState(initialValue);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useUpdateEffect(() => {
    setIsChecked(initialValue);
  }, [initialValue]);

  const handleChange = useCallback(async () => {
    setIsSubmitting(true);
    await onChange(!isChecked);
    setIsChecked(!isChecked);
    setIsSubmitting(false);
  }, [isChecked, onChange]);

  return [isChecked, handleChange, isSubmitting];
};

export const Checkboxes = ({ hiddenFromClients, pinned, onChange, className, type }) => {
  const { t } = useTranslation();
  const isUserClientSelector = useSelector(isLoggedUserClientSelector);
  const hideFromClients = useSelector((state) => getRawConfigOption(state, "hide_from_clients_enabled"));
  const id = useMemo(() => `${type}_${v4()}`, [type]);

  const [isHiddenFromClientsChecked, handleHiddenFromClientsChange, isHiddenFromClientsSubmitting] = useCheckboxToggle(
    hiddenFromClients,
    (value) => onChange({ is_hidden_from_clients: value })
  );

  const [isPinnedChecked, handlePinnedChange, isPinnedSubmitting] = useCheckboxToggle(pinned, (value) =>
    onChange({ is_pinned: value })
  );

  if (isUserClientSelector) return null;

  return (
    <div className={className}>
      {hideFromClients ? (
        <FormCheckbox
          id={`${id}_panel_hidden_for_clients`}
          checked={isHiddenFromClientsChecked}
          onChange={handleHiddenFromClientsChange}
          text={t("Hidden from clients")}
          disabled={isHiddenFromClientsSubmitting}
        />
      ) : null}
      <FormCheckbox
        id={`${id}_panel_pinned`}
        checked={isPinnedChecked}
        onChange={handlePinnedChange}
        className="tw-mt-2"
        text={t("Pinned to top")}
        disabled={isPinnedSubmitting}
      />
    </div>
  );
};

Checkboxes.propTypes = {
  hiddenFromClients: PropTypes.bool,
  pinned: PropTypes.bool,
  onChange: PropTypes.func,
  className: PropTypes.string,
  type: PropTypes.string,
};

Checkboxes.defaultProps = {
  type: "discussion",
};
